import React from 'react';
import { ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { getKpiStatusColor } from '../../utils/colorHelpers';

interface KPIMetric {
    id: string;
    title: string;
    value: string | number;
    unit?: string;
    target?: number;
    status: 'good' | 'warning' | 'critical';
    trend: 'up' | 'down' | 'stable';
    trendValue: string;
    icon: React.ReactNode;
    sparklineData?: Array<{ value: number }>;
}

const getTrendIcon = (trend: string) => {
    switch (trend) {
        case 'up': return <ArrowUp className="w-4 h-4" />;
        case 'down': return <ArrowDown className="w-4 h-4" />;
        default: return <Minus className="w-4 h-4" />;
    }
};

export const KPICard: React.FC<{ metric: KPIMetric }> = ({ metric }) => {
    return (
        <div className="bg-white dark:bg-slate-800 rounded-xl p-5 shadow-lg hover:shadow-xl transition-all border border-gray-200 dark:border-slate-700">
            <div className="flex items-start justify-between mb-3">
                <div className={`p-3 rounded-lg ${metric.status === 'good' ? 'bg-green-100 text-green-600' :
                    metric.status === 'warning' ? 'bg-amber-100 text-amber-600' :
                        'bg-red-100 text-red-600'
                    }`}>
                    {metric.icon}
                </div>
                <div className={`w-3 h-3 rounded-full ${getKpiStatusColor(metric.status)} animate-pulse`} />
            </div>

            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                {metric.title}
            </h3>

            <div className="flex items-baseline gap-2 mb-2">
                <span className="text-3xl font-bold text-gray-900 dark:text-white">
                    {metric.value}
                </span>
                {metric.unit && (
                    <span className="text-lg text-gray-500 dark:text-gray-400">
                        {metric.unit}
                    </span>
                )}
            </div>

            <div className="flex items-center justify-between">
                <div className={`flex items-center gap-1 text-sm font-medium ${metric.trend === 'up' && metric.status === 'good' ? 'text-green-600' :
                    metric.trend === 'down' && metric.id === 'mttr' ? 'text-green-600' :
                        metric.trend === 'up' ? 'text-red-600' :
                            metric.trend === 'down' ? 'text-green-600' :
                                'text-gray-600'
                    }`}>
                    {getTrendIcon(metric.trend)}
                    <span>{metric.trendValue}</span>
                </div>
                {metric.target && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                        Target: {metric.target}{metric.unit}
                    </div>
                )}
            </div>

            {metric.sparklineData && (
                <div className="mt-3 h-8">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={metric.sparklineData}>
                            <Line
                                type="monotone"
                                dataKey="value"
                                stroke={metric.status === 'good' ? '#10B981' : metric.status === 'warning' ? '#F59E0B' : '#EF4444'}
                                strokeWidth={2}
                                dot={false}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
};
